<template>
  <div>
    <student-blacklist
      @refetch-data="$emit('refetch-data')"
      :is-student-blacklist-open.sync="isStudentBlacklistOpen"
      :student-data.sync="studentData"
      :profile-data.sync="profileData"
    ></student-blacklist>
    <student-re-enrollment-eligibility
      @refetch-data="$emit('refetch-data')"
      :is-open.sync="isReEnrollmentEligibilityOpen"
      :student-data="studentData"
      :profile-data="profileData"
    />
    <student-merger :is-student-merger-open.sync="isStudentMergerOpen" :student-data.sync="studentData"></student-merger>

    <div class="card card-bg" @contextmenu.prevent="$refs.studentViewEditMenu.open">
      <div class="card-body rounded z-2 position-relative">
        <div class="d-flex align-items-center">
          <div class="d-flex align-items-center mr-4">
            <b-avatar
              :src="studentData.avatar"
              :text="avatarText(studentData.name)"
              size="80px"
              variant="light-info"
              style="margin-top: 1px"
              class="mr-1"
            />
            <div class="d-flex flex-column align-items-start justify-content-center">
              <b-link class="d-flex align-items-center text-white">
                <small class="" style="font-size: 1.15rem; line-height: 1.35rem; margin-bottom: 1px"> {{ studentData.myIpId }} </small>
              </b-link>

              <div class="d-flex align-items-center mb-25">
                <h4
                  class="font-weight-bolder tracking-tighter mr-50"
                  style="font-size: 1.85rem; line-height: 2.15rem; margin-bottom: 2px"
                  @click="copyElementToClipboard('fullName', 'name')"
                >
                  {{ studentData.title ? `${studentData.title}. ${studentData.fullName}` : studentData.fullName }}
                </h4>
                <feather-icon
                  v-if="$can('student.update')"
                  icon="EditIcon"
                  size="13"
                  class="cursor-pointer text-muted"
                  style="margin-bottom: 2px"
                  @click="$router.push({ name: 'apps-students-edit', params: { id: studentData.id } })"
                />
              </div>

              <div class="d-flex align-items-center">
                <div class="d-flex align-items-center">
                  <b-badge class="profile-badge mr-25" :variant="`${resolveStudentStatusVariant(studentData.status)}`">
                    <feather-icon :icon="resolveStudentStatusIcon(studentData.status)" size="12" />
                    {{ title(studentData.status) }}
                  </b-badge>
                </div>
              </div>
            </div>
          </div>
          <table class="mt-0">
            <tr @click="copyElementToClipboard('email', 'email')">
              <th class="pb-50">
                <feather-icon icon="MailIcon" class="mr-75" />
                <span class="font-weight-bold">Email</span>
              </th>
              <td class="pb-50 pl-2" style="color: #d0d2d6 !important">
                {{ studentData.email }}
              </td>
            </tr>
            <tr @click="copyElementToClipboard('phoneNumber', 'phone number')">
              <th class="pb-50">
                <feather-icon icon="PhoneIcon" class="mr-75" />
                <span class="font-weight-bold">Contact</span>
              </th>
              <td class="pb-50 pl-2 text-capitalize" style="color: #d0d2d6 !important">
                {{ studentData.phoneNumber }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon icon="CheckIcon" class="mr-75" />
                <span class="font-weight-bold">Status</span>
              </th>
              <td class="pb-50 pl-2 text-capitalize" style="color: #d0d2d6 !important">
                {{ studentData.status }}
              </td>
            </tr>
            <tr>
              <th class="pb-0">
                <feather-icon icon="ShieldIcon" class="mr-75" />
                <span class="font-weight-bold">Onboarded?</span>
              </th>
              <td class="pb-0 pl-2 text-capitalize">
                <b-badge pill class="profile-badge mr-25" :variant="profileData.attributes.onboardingCompleted ? 'success' : 'danger'">
                  {{ profileData.attributes.onboardingCompleted ? 'Yes' : 'No' }}
                </b-badge>
              </td>
            </tr>
          </table>
          <table class="mt-0">
            <tr>
              <th class="pb-50">
                <feather-icon icon="ShieldIcon" class="mr-75" />
                <span class="font-weight-bold">MyIP</span>
              </th>
              <td class="pb-50 pl-2 text-capitalize">
                <b-badge pill class="profile-badge mr-25" :variant="!!studentData.metadata.myIpUserId ? 'success' : 'danger'">
                  {{ !!studentData.metadata.myIpUserId ? 'Yes' : 'No' }}
                </b-badge>
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon icon="BookIcon" class="mr-75" />
                <span class="font-weight-bold">Learn</span>
              </th>
              <td class="pb-50 pl-2 text-capitalize">
                <b-badge pill class="profile-badge mr-25" :variant="!!studentData.metadata.learnId ? 'success' : 'danger'">
                  {{ !!studentData.metadata.learnId ? 'Yes' : 'No' }}
                </b-badge>
              </td>
            </tr>
            <tr
              @click="viewStudentIdentity"
              :class="{ 'cursor-pointer': profileData.identity && profileData.identity.documentIds.length > 0 }"
            >
              <th class="pb-50">
                <feather-icon icon="CameraIcon" class="mr-75" />
                <span class="font-weight-bold">Identity</span>
              </th>
              <td class="pb-50 pl-2 text-capitalize">
                <b-badge pill class="profile-badge mr-25" :variant="identityVariant()">
                  {{ title(studentData.verification) }}
                </b-badge>
              </td>
            </tr>
            <tr @click="viewStudentDera" :class="{ 'cursor-pointer': canAccessStudentDera }">
              <th class="pb-0">
                <feather-icon icon="MonitorIcon" class="mr-75" />
                <span class="font-weight-bold">DERA</span>
              </th>
              <td class="pb-0 pl-2 text-capitalize">
                <b-badge pill class="profile-badge mr-25" :variant="deraVariant()">
                  {{ deraLabel() }}
                </b-badge>
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <vue-context ref="studentViewEditMenu" style="min-width: 11rem">
      <li v-if="$can('student.update')">
        <b-link
          class="d-flex align-items-center justify-content-between"
          @click="$router.push({ name: 'apps-students-edit', params: { id: studentData.id } })"
        >
          <span>Edit</span>
          <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
        </b-link>
      </li>

      <li class="v-context__sub">
        <a>
          <span> Manage </span>
        </a>
        <ul class="v-context">
          <li v-if="$can('student.merge')">
            <b-link class="d-flex align-items-center justify-content-between" @click="startStudentMerge">
              <span>Merge</span>
              <feather-icon icon="GitMergeIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>
          <li v-if="$can('student.blacklist.remove') && profileData.blacklist && profileData.blacklist.enabled">
            <b-link class="d-flex align-items-center justify-content-between" @click="startStudentUnBlacklist">
              <span>Un-Blacklist</span>
              <font-awesome-icon icon="fa-regular fa-face-smile-halo" class="ml-50 text-muted" width="14" />
            </b-link>
          </li>
          <li v-if="$can('student.blacklist.add') && ((profileData.blacklist && !profileData.blacklist.enabled) || !profileData.blacklist)">
            <b-link class="d-flex align-items-center justify-content-between" @click="startStudentBlacklist">
              <span>Blacklist</span>
              <font-awesome-icon icon="fa-regular fa-face-swear" class="ml-50 text-muted" width="14" />
            </b-link>
          </li>
          <li v-if="$can('student.blacklist.add')">
            <b-link class="d-flex align-items-center justify-content-between" @click="isReEnrollmentEligibilityOpen = true">
              <span>Re-Enrollment Eligibility</span>
              <font-awesome-icon icon="fa-regular fa-flag" class="ml-50 text-muted" width="14" />
            </b-link>
          </li>
        </ul>
      </li>

      <li class="v-context__sub">
        <a>
          <span> MyIP </span>
        </a>
        <ul class="v-context">
          <li v-if="studentData.status !== 'onboarded'">
            <b-link class="d-flex align-items-center justify-content-between" @click="startSendOnboarding">
              <span>Send Onboarding</span>
              <feather-icon icon="SendIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>

          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="startSendLogin">
              <span>Send Login</span>
              <feather-icon icon="LogInIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>
          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="startResetPassword">
              <span>Reset Password</span>
              <feather-icon icon="UnlockIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>

          <li><hr class="my-0" /></li>

          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="sendAthenaLogin">
              <span>Send Athena</span>
              <feather-icon icon="UnlockIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>
        </ul>
      </li>

      <li class="v-context__sub" v-if="studentData.verification !== 'verified' && canAccessStudentIdentity">
        <a>
          <span> Identity </span>
        </a>
        <ul class="v-context">
          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="startSendVerification">
              <span>Send Verify</span>
            </b-link>
          </li>

          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="markAsVerified">
              <span>Mark Verified</span>
            </b-link>
          </li>

          <li><hr class="my-0" /></li>

          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="copyVerification('identity')">
              <span>Open w/ Identity</span>
            </b-link>
          </li>
          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="copyVerification('ssn')">
              <span>Open w/ SSN</span>
            </b-link>
          </li>
        </ul>
      </li>

      <li class="v-context__sub" v-if="canAccessStudentDera">
        <a>
          <span> DERA </span>
        </a>
        <ul class="v-context">
          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="startSendDera">
              <span>{{ deraSendLabel }}</span>
              <feather-icon icon="SendIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>

          <li v-if="hasDeraInvite">
            <b-link class="d-flex align-items-center justify-content-between" @click="startResendDera">
              <span>Resend Email</span>
              <feather-icon icon="MailIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>

          <li v-if="hasDeraInvite">
            <b-link class="d-flex align-items-center justify-content-between" @click="startExtendDera">
              <span>Extend 14 Days</span>
              <feather-icon icon="ClockIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>

          <li><hr class="my-0" /></li>

          <li>
            <b-link class="d-flex align-items-center justify-content-between" @click="viewStudentDera">
              <span>View Details</span>
              <feather-icon icon="ExternalLinkIcon" size="15" class="ml-50 text-muted" />
            </b-link>
          </li>
        </ul>
      </li>
    </vue-context>

    <div
      v-if="profileData.blacklist && profileData.blacklist.enabled"
      :style="`${calculateBorderColor('#ea5455')} padding: 0.075rem`"
      class="mb-1 rounded position-relative overflow-hidden drop-shadow-default"
    >
      <div class="rounded" style="background: #191a22">
        <div class="card-body rounded z-2 position-relative" :style="calculateBackgroundColor('#ea5455')">
          <b-col md="12" lg="12">
            <div>
              <span class="d-flex align-items-center mb-25">
                <feather-icon icon="AlertTriangleIcon" size="24" class="mr-50 text-warning" />
                <h3 class="font-weight-bolder mb-0">Blacklisted</h3>
              </span>

              <p class="mb-0 text-light">
                Blacklisted since {{ momentTz(profileData.blacklist.timestamp).format('L') }} for the following reason:
              </p>

              <div>
                {{ profileData.blacklist.reason || 'No reason specified' }}
              </div>
            </div>
          </b-col>
        </div>
      </div>
    </div>

    <div
      v-if="reEnrollmentMarker"
      :style="`${calculateBorderColor(reEnrollmentColor)} padding: 0.075rem`"
      class="mb-1 rounded position-relative overflow-hidden drop-shadow-default"
    >
      <div class="rounded" style="background: #191a22">
        <div class="card-body rounded z-2 position-relative" :style="calculateBackgroundColor(reEnrollmentColor)">
          <b-col md="12" lg="12">
            <span class="d-flex align-items-center mb-25">
              <font-awesome-icon icon="fa-regular fa-flag" :class="`mr-50 text-${reEnrollmentMarker}`" width="20" />
              <h3 class="font-weight-bolder mb-0">Re-Enrollment Eligible: {{ reEnrollmentLabel }}</h3>
            </span>
            <p class="mb-0 text-light">
              {{
                reEnrollmentMarker === 'danger'
                  ? 'This student is not eligible for re-enrollment.'
                  : 'Re-enrollment requires an appeal or additional documentation.'
              }}
              <template v-if="reEnrollment.timestamp">Set {{ momentTz(reEnrollment.timestamp).format('L') }}.</template>
            </p>
            <div v-if="reEnrollment.note" style="white-space: pre-line">{{ reEnrollment.note }}</div>
          </b-col>
        </div>
      </div>
    </div>

    <student-identity-modal
      v-if="isStudentIdentityOpen && $can('student.profile.identity')"
      :student-identity-open.sync="isStudentIdentityOpen"
      :identity-data="profileData.identity"
    />

    <student-dera-modal
      v-if="isStudentDeraOpen && $can('student.profile.dera')"
      :student-dera-open.sync="isStudentDeraOpen"
      :dera-data="profileData.dera || {}"
      :student-id="studentData.id"
      @refresh="$emit('refetch-data')"
    />
  </div>
</template>

<script>
import { BAvatar, BBadge, BButton, BCard, BCol, BDropdown, BDropdownItem, BDropdownItemButton, BLink, BRow } from 'bootstrap-vue';
import { avatarText, title } from '@core/utils/filter';
import StudentIdentityModal from '@/views/apps/student/students-view/components/StudentIdentityModal';
import StudentDeraModal from '@/views/apps/student/students-view/components/StudentDeraModal';
import ToastificationContent from '@core/components/toastification/ToastificationContent';
import StudentMerger from '@/views/apps/student/students-view/components/StudentMerger';
import StudentBlacklist from './components/StudentBlacklist';
import StudentReEnrollmentEligibility from './components/StudentReEnrollmentEligibility';
import { resolveReEnrollmentLabel, resolveReEnrollmentMarker } from '@/views/apps/roster/profile/profileOptions';
import { hexToRGB } from '@/libs/utils/color';
import VueContext from 'vue-context';
import useStudents from '../useStudents';
import { momentTz } from '@/libs/utils/time';

export default {
  components: {
    StudentMerger,
    StudentBlacklist,
    StudentReEnrollmentEligibility,
    BBadge,
    BCard,
    BButton,
    BRow,
    BCol,
    BLink,
    BAvatar,
    BDropdownItem,
    BDropdownItemButton,
    BDropdown,
    VueContext,

    StudentIdentityModal,
    StudentDeraModal,
  },
  props: {
    studentData: {
      type: Object,
      required: true,
    },
    profileData: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      studentColor: '#5b5b5e',
      isStudentBlacklistOpen: false,
      isReEnrollmentEligibilityOpen: false,
      isStudentMergerOpen: false,
      isStudentIdentityOpen: false,
      isStudentDeraOpen: false,
    };
  },
  metaInfo() {
    const title = this.studentData.fullName ? `${this.studentData.fullName}` : 'View Student';
    return {
      title,
    };
  },
  methods: {
    calculateBackgroundColor(hex) {
      const rgb = hexToRGB(hex);
      return `background: linear-gradient(200deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.35) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05) 93.34%)`;
    },
    calculateBorderColor(hex) {
      const rgb = hexToRGB(hex);
      return `background: linear-gradient(190deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.03) 100%);`;
    },
    startResetPassword() {
      this.$swal({
        title: 'Are you sure?',
        text: 'An email will be sent to the student, requiring them to reset thier password.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Reset Password',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          this.$store
            .dispatch('studentStoreModule/resetStudentMyIPPassword', {
              studentId: this.studentData.id,
            })
            .then(() => {
              this.$emit('refetch-data');

              this.$swal({
                icon: 'success',
                title: 'Password Reset!',
                text: 'This student has been sent an email to reset their password',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              });
            });
        }
      });
    },
    startSendLogin() {
      this.$swal({
        title: 'Are you sure?',
        text: 'An email will be sent to the student, giving them a magic link to login.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Send Login',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          this.$store
            .dispatch('studentStoreModule/sendStudentMyIPLogin', {
              studentId: this.studentData.id,
            })
            .then(() => {
              this.$emit('refetch-data');

              this.$swal({
                icon: 'success',
                title: 'Login Sent!',
                text: 'This student has been sent an email to login',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              });
            });
        }
      });
    },
    startSendOnboarding() {
      this.$swal({
        title: 'Are you sure?',
        text: 'An email will be sent to the student, giving them a magic link to onboard.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Send Onboarding',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          this.$store
            .dispatch('studentStoreModule/sendStudentMyIPOnboarding', {
              studentId: this.studentData.id,
            })
            .then(() => {
              this.$emit('refetch-data');

              this.$swal({
                icon: 'success',
                title: 'Login Sent!',
                text: 'This student has been sent an email to onboard',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              });
            });
        }
      });
    },
    async markAsVerified() {
      if (!this.canAccessStudentIdentity || this.studentData.verification === 'verified') return;

      const { value: reason } = await this.$swal({
        title: 'Manual Identity Verify Override',
        text: 'This bypasses Stripe identity verification. Enter a reason to continue.',
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: 'Required reason',
        inputAttributes: {
          autocapitalize: 'off',
          autocorrect: 'off',
        },
        showCancelButton: true,
        confirmButtonText: 'Mark Verified',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
        inputValidator: (value) => {
          if (!value || !value.trim()) return 'Reason is required';
          return null;
        },
      });

      if (!reason || !reason.trim()) return;

      return this.$store
        .dispatch('studentStoreModule/markStudentIdentityVerifiedManual', {
          studentId: this.studentData.id,
          reason: reason.trim(),
        })
        .then(() => {
          this.$emit('refetch-data');

          this.$swal({
            icon: 'success',
            title: 'Identity Verified',
            text: 'The student has been marked as verified using manual override.',
            customClass: {
              confirmButton: 'btn btn-success',
            },
          });
        })
        .catch((error) => {
          const message = error && error.response && error.response.data ? error.response.data.message : 'Unknown error';

          this.$swal({
            icon: 'error',
            title: 'Failed to mark verified',
            text: message,
            customClass: {
              confirmButton: 'btn btn-danger',
            },
          });
        });
    },
    startSendVerification() {
      if (!this.canAccessStudentIdentity) return;

      this.$swal({
        title: 'Are you sure?',
        text: 'An email will be sent to the student giving them a MyIP verification link. This link will automatically prompt them to verify their account.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Send Verification Request',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          this.$store
            .dispatch('studentStoreModule/sendStudentMyIPVerification', {
              studentId: this.studentData.id,
            })
            .then(() => {
              this.$emit('refetch-data');

              this.$swal({
                icon: 'success',
                title: 'Login Sent!',
                text: 'This student has been sent an email to verify their identity',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              });
            });
        }
      });
    },

    copyVerification(type = 'identity') {
      if (!this.canAccessStudentIdentity) return;

      this.$store
        .dispatch('studentStoreModule/createStudentIdentitySession', {
          studentId: this.studentData.id,
          type,
        })
        .then((response) => {
          const { data } = response;
          if (!data.url) {
            return this.$swal({
              title: 'Failed to request identity verification',
              text: 'An unknown error occurred while attempting to obtain the verification link',
            });
          }

          this.copyTextToClipboard(data.url);
          window.open(data.url, '_blank');
        })
        .catch((error) => {
          console.error('Failed to request verification');
        });
    },

    sendAthenaLogin() {
      this.$swal({
        title: 'Are you sure?',
        text: 'An email will be sent to the student, giving them a magic link to Athena.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Send Athena',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          this.$store
            .dispatch('studentStoreModule/sendStudentAthenaLogin', {
              studentId: this.studentData.id,
            })
            .then(() => {
              this.$emit('refetch-data');

              this.$swal({
                icon: 'success',
                title: 'Login Sent!',
                text: 'This student has been sent an email to access athena',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              });
            })
            .catch((error) => {
              if (error.response) {
                const { response } = error;

                this.$swal({
                  icon: 'error',
                  title: 'Failed to send login!',
                  text: `Sending fail: ${response.data.message}`,
                  customClass: {
                    confirmButton: 'btn btn-success',
                  },
                });
              }
            });
        }
      });
    },

    startStudentBlacklist() {
      this.isStudentBlacklistOpen = true;
    },
    startStudentUnBlacklist() {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to unblacklist this student?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Forgive Them',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          this.$store
            .dispatch('studentStoreModule/updateStudentProfile', {
              id: this.studentData.id,
              profileData: {
                blacklist: { enabled: false },
              },
            })
            .then(() => {
              this.$emit('refetch-data');

              this.$swal({
                icon: 'success',
                title: 'Un-Blacklisted!',
                text: 'This student has been removed from the blacklist',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              });
            });
        }
      });
    },
    copyTextToClipboard(text) {
      return this.$copyText(text)
        .then(() =>
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Copied to clipboard',
              icon: 'ClipboardIcon',
            },
          }),
        )
        .catch((e) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Could not copy!',
              icon: 'AlertCircleIcon',
            },
          });
        });
    },
    copyElementToClipboard(id) {
      return this.copyTextToClipboard(this.studentData[id]);
    },
    viewStudentIdentity() {
      if (this.canAccessStudentIdentity && this.profileData.identity && this.profileData.identity.documentIds.length > 0) {
        this.isStudentIdentityOpen = true;
      }
    },
    startStudentMerge() {
      this.isStudentMergerOpen = true;
    },
    identityVariant() {
      switch (this.studentData.verification) {
        case 'verified':
          return 'success';
        case 'requested':
          return 'info';
        case 'processing':
          return 'info';
        case 'action_required':
          return 'warning';
        case 'unverified':
        case 'failed':
          return 'danger';
        default:
          return 'warning';
      }
    },
    deraVariant() {
      const status = (this.profileData.dera && this.profileData.dera.status) || 'not_sent';
      switch (status) {
        case 'passed':
          return 'success';
        case 'invited':
          return 'info';
        case 'started':
          return 'warning';
        case 'failed':
          return 'danger';
        case 'not_sent':
        default:
          return 'secondary';
      }
    },
    deraLabel() {
      const dera = this.profileData.dera;
      if (!dera || !dera.status || dera.status === 'not_sent') return 'Not sent';

      if (dera.status === 'passed' && dera.latest && typeof dera.latest.percentage === 'number') {
        return `Passed (${dera.latest.percentage}%)`;
      }
      if (dera.status === 'failed' && dera.latest && typeof dera.latest.percentage === 'number') {
        return `Failed (${dera.latest.percentage}%)`;
      }

      return this.title(dera.status);
    },
    // The three DERA actions below follow this file's existing pattern for
    // context-menu actions: confirm, dispatch, refetch, report. Replacing a
    // link invalidates the one already emailed, so that confirmation says so.
    runDeraAction({ title, text, confirmButtonText, action, params, successTitle, successText }) {
      this.$swal({
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText,
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      }).then(async (result) => {
        if (!result.value) return;

        this.$store
          .dispatch(`studentStoreModule/${action}`, { studentId: this.studentData.id, ...params })
          .then((response) => {
            this.$emit('refetch-data');

            const alreadyPassed = response && response.data && response.data.alreadyPassed;
            this.$swal({
              icon: 'success',
              title: alreadyPassed ? 'Already Passed' : successTitle,
              text: alreadyPassed
                ? 'This student had already passed, so their existing DERA record was linked.'
                : successText,
              customClass: { confirmButton: 'btn btn-success' },
            });
          })
          .catch((error) => {
            this.$swal({
              icon: 'error',
              title: 'Failed',
              text: (error.response && error.response.data && error.response.data.message) || 'Please try again.',
              customClass: { confirmButton: 'btn btn-danger' },
            });
          });
      });
    },
    startSendDera() {
      const replacing = this.hasDeraInvite;
      this.runDeraAction({
        title: replacing ? 'Replace the existing link?' : 'Send a DERA link?',
        text: replacing
          ? 'A new assessment link will be emailed. Any link already sent to this student will stop working.'
          : 'An email will be sent to the student with a link to sit the readiness assessment.',
        confirmButtonText: this.deraSendLabel,
        action: 'sendDeraInvite',
        successTitle: 'Link Sent!',
        successText: 'The student has been emailed a DERA assessment link.',
      });
    },
    startResendDera() {
      this.runDeraAction({
        title: 'Resend the DERA email?',
        text: 'The same assessment link will be emailed to the student again.',
        confirmButtonText: 'Resend Email',
        action: 'resendDeraInvite',
        successTitle: 'Email Resent!',
        successText: 'The student has been emailed their DERA link again.',
      });
    },
    startExtendDera() {
      this.runDeraAction({
        title: 'Extend the DERA invite?',
        text: 'The existing link will stay valid for a further 14 days.',
        confirmButtonText: 'Extend 14 Days',
        action: 'extendDeraExpiry',
        params: { extendDays: 14 },
        successTitle: 'Expiry Extended!',
        successText: 'The invite is now valid for another 14 days.',
      });
    },
    viewStudentDera() {
      if (this.canAccessStudentDera) {
        this.isStudentDeraOpen = true;
      }
    },
  },
  computed: {
    reEnrollment() {
      return this.profileData.reEnrollment || {};
    },
    reEnrollmentMarker() {
      return resolveReEnrollmentMarker(this.reEnrollment.eligibility);
    },
    reEnrollmentLabel() {
      return resolveReEnrollmentLabel(this.reEnrollment.eligibility);
    },
    reEnrollmentColor() {
      return this.reEnrollmentMarker === 'danger' ? '#ea5455' : '#ff9f43';
    },
    canAccessStudentIdentity() {
      return this.$can('student.profile.identity');
    },
    canAccessStudentDera() {
      return this.$can('student.profile.dera');
    },
    hasDeraInvite() {
      return Boolean(this.profileData.dera && this.profileData.dera.deraStudentId);
    },
    // A student who failed needs a retake link, which is the same action with
    // a name that says what it is actually for.
    deraSendLabel() {
      if (!this.hasDeraInvite) return 'Send Link';
      return this.profileData.dera && this.profileData.dera.status === 'failed' ? 'Send Retake Link' : 'Replace Link';
    },
  },
  setup() {
    const { resolveStudentStatusVariant, resolveStudentStatusIcon } = useStudents();

    return {
      resolveStudentStatusVariant,
      resolveStudentStatusIcon,
      avatarText,
      hexToRGB,
      momentTz,
      title,
    };
  },
};
</script>

<style></style>
